import { ChangeDetectionStrategy, Component } from '@angular/core';
import { RouterLink } from '@angular/router';
import { SellGuideCardComponent } from '@app/guides/sell-guide-card/sell-guide-card.component';
import { ASSET_PATHS } from '@app/constants/asset-paths';

interface SellerGuideResource {
  title: string;
  description: string;
  link: string;
  linkLabel: string;
}

interface SellerGuideTip {
  title: string;
  description: string;
}

@Component({
  standalone: true,
  imports: [SellGuideCardComponent, RouterLink],
  selector: 'app-seller-guide',
  templateUrl: './seller-guide.component.html',
  styleUrls: ['./seller-guide.component.css'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class SellerGuideComponent {
  readonly assetPaths = ASSET_PATHS;

  readonly highlights = [
    'Accurate pricing backed by local comps',
    'MLS listing plus online & social marketing',
    'Support from list date through closing',
  ];

  readonly prepItems = [
    {
      title: 'Curb appeal',
      detail: 'First impressions drive showings. Fresh paint, landscaping, and a clean entryway help buyers picture themselves moving in.',
    },
    {
      title: 'Declutter & stage',
      detail: 'Clear countertops, depersonalize rooms, and open up traffic flow so spaces feel larger in photos and in person.',
    },
    {
      title: 'Repairs & disclosures',
      detail: 'Address obvious fixes before listing and gather disclosure documents early — Texas sellers must share known property conditions.',
    },
    {
      title: 'Pricing strategy',
      detail: 'Price too high and days on market climb; too low leaves money on the table. Your agent recommends a range based on real sales data.',
    },
    {
      title: 'Showing plan',
      detail: 'Flexible showing windows and a spotless home on tour day keep buyer interest high while your agent coordinates feedback.',
    },
  ];

  readonly marketingBenefits = [
    {
      title: 'Online MLS exposure',
      description:
        'Your listing syndicates to major real estate sites with professional photos, accurate details, and neighborhood context buyers search for.',
    },
    {
      title: 'Mobile-ready search',
      description:
        'Buyers browse Gabali listings on any device — the same map and filter tools they use on desktop, optimized for phones and tablets.',
    },
    {
      title: 'Social & local marketing',
      description:
        'Our agents promote listings through social channels and local networks to reach buyers who may not be actively searching yet.',
    },
    {
      title: 'High-quality photography',
      description:
        'Strong listing photos are the first showing. We emphasize visuals that highlight your home’s best features and floor plan flow.',
    },
  ];

  readonly resources: SellerGuideResource[] = [
    {
      title: "What's My Home Worth?",
      description:
        'Start with a market snapshot based on recent sales near your address before you commit to a list price.',
      link: '/home-estimate-page',
      linkLabel: 'Get an estimate',
    },
    {
      title: 'Meet Our Agents',
      description:
        'Connect with a listing specialist who knows your neighborhood and can walk you through prep, pricing, and timing.',
      link: '/agents',
      linkLabel: 'View agents',
    },
    {
      title: 'Request a Consultation',
      description:
        'Not ready to list yet? Tell us about your property and timeline — we will follow up with a tailored selling plan.',
      link: '/contact-page',
      linkLabel: 'Contact us',
    },
    {
      title: 'See How We Market Listings',
      description:
        'Browse active Gabali listings to see how properties are presented to buyers searching in Laredo and South Texas.',
      link: '/featured-properties',
      linkLabel: 'Browse listings',
    },
  ];

  readonly localTips: SellerGuideTip[] = [
    {
      title: 'Price to the market, not the estimate',
      description:
        'Automated valuations miss condition, upgrades, and hyper-local demand. Sold comps within your subdivision are the best guide.',
    },
    {
      title: 'Seasonality in South Texas',
      description:
        'Spring and early summer often bring more buyer activity, but well-priced homes sell year-round when marketed correctly.',
    },
    {
      title: 'Investor & relocation buyers',
      description:
        'Laredo attracts investor and job-relocation buyers. Flexible closing timelines and clear property disclosures can widen your offer pool.',
    },
    {
      title: 'Negotiate beyond price',
      description:
        'Closing costs, repair credits, and possession dates matter as much as the offer amount. Your agent helps you compare net proceeds, not just top-line price.',
    },
  ];
}
